/**
 * Admin — question bank, lookups (homes/regions/positions), access, settings.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { QuestionAdmin, LookupRow } from "../api/types";

type Tab = "questions" | "lookups" | "access" | "settings";

export default function AdminPage() {
  const [tab, setTab] = useState<Tab>("questions");
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-liaise-navy">Admin</h1>
      <div className="flex gap-2 flex-wrap">
        {(["questions", "lookups", "access", "settings"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize transition ${
              tab === t ? "bg-liaise-navy text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-liaise-navy"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "questions" && <QuestionsTab />}
      {tab === "lookups" && <LookupsTab />}
      {tab === "access" && <AccessTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

/* ------------------------------------------------------------- questions */

const EMPTY_Q: Omit<QuestionAdmin, "id" | "sp_id"> = {
  q_no: 1,
  section: "english",
  kind: "mcq",
  question: "",
  passage: null,
  options: null,
  correct_answer: "",
  evaluator_hint: "",
  marks: 1,
  active: true,
};

function QuestionsTab() {
  const qc = useQueryClient();
  const { data: questions } = useQuery({ queryKey: ["adminQuestions"], queryFn: api.manage.questions.list });
  const [editing, setEditing] = useState<QuestionAdmin | "new" | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["adminQuestions"] });
  const deactivateMut = useMutation({ mutationFn: api.manage.questions.deactivate, onSuccess: refresh });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">
          The live paper. Changes apply to NEW sittings only - submitted answers keep their snapshot.
        </p>
        <button className="btn-primary" onClick={() => setEditing("new")}>+ Add question</button>
      </div>
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="px-4 py-2">Q</th>
              <th className="px-4 py-2">Section</th>
              <th className="px-4 py-2">Kind</th>
              <th className="px-4 py-2">Question</th>
              <th className="px-4 py-2">Correct answer</th>
              <th className="px-4 py-2">Marks</th>
              <th className="px-4 py-2">Active</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(questions ?? []).map((row) => (
              <tr key={row.id} className={`border-b border-slate-100 ${!row.active ? "opacity-40" : ""}`}>
                <td className="px-4 py-2 font-bold text-liaise-navy">{row.q_no}</td>
                <td className="px-4 py-2 capitalize">{row.section}</td>
                <td className="px-4 py-2 capitalize">{row.kind}</td>
                <td className="px-4 py-2 max-w-md"><span className="line-clamp-2">{row.question}</span></td>
                <td className="px-4 py-2 max-w-[180px] truncate">{row.correct_answer ?? "-"}</td>
                <td className="px-4 py-2">{row.marks}</td>
                <td className="px-4 py-2">{row.active ? "Yes" : "No"}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button className="text-liaise-blue hover:underline text-xs mr-3" onClick={() => setEditing(row)}>Edit</button>
                  {row.active && (
                    <button
                      className="text-liaise-red hover:underline text-xs"
                      onClick={() => { if (confirm(`Deactivate Q${row.q_no}?`)) deactivateMut.mutate(row.id); }}
                    >
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <QuestionEditor
          initial={editing === "new" ? EMPTY_Q : editing}
          id={editing === "new" ? null : editing.id}
          onClose={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function QuestionEditor({
  initial,
  id,
  onClose,
}: {
  initial: Omit<QuestionAdmin, "id" | "sp_id">;
  id: number | null;
  onClose: () => void;
}) {
  const [f, setF] = useState({ ...initial, optionsText: (initial.options ?? []).join("\n") });
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    const usesOptions = f.kind === "mcq" || f.kind === "ranking" || f.kind === "multi_text" || f.kind === "multi_select";
    const options = usesOptions ? f.optionsText.split("\n").map((s) => s.trim()).filter(Boolean) : null;
    const body = {
      q_no: Number(f.q_no),
      section: f.section,
      kind: f.kind,
      question: f.question,
      passage: f.passage?.trim() || null,
      options,
      correct_answer: f.kind === "writing" ? null : f.correct_answer || null,
      evaluator_hint: f.evaluator_hint || null,
      marks: Number(f.marks),
      active: f.active,
    };
    try {
      if (id == null) await api.manage.questions.create(body);
      else await api.manage.questions.update(id, body);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-liaise-navy">{id == null ? "Add question" : `Edit question ${initial.q_no}`}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="label">Number</label>
            <input type="number" className="input" value={f.q_no} onChange={(e) => setF({ ...f, q_no: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Section</label>
            <select className="input" value={f.section} onChange={(e) => setF({ ...f, section: e.target.value as QuestionAdmin["section"] })}>
              <option value="english">English</option>
              <option value="maths">Maths</option>
              <option value="writing">Writing</option>
            </select>
          </div>
          <div>
            <label className="label">Kind</label>
            <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as QuestionAdmin["kind"] })}>
              <option value="mcq">Multiple choice</option>
              <option value="text">Text (exact match)</option>
              <option value="numeric">Numeric</option>
              <option value="ranking">Put in order</option>
              <option value="multi_select">Pick several options</option>
              <option value="multi_text">Several short answers</option>
              <option value="writing">Writing (human-marked)</option>
            </select>
          </div>
          <div>
            <label className="label">Marks</label>
            <input type="number" step="0.5" className="input" value={f.marks} onChange={(e) => setF({ ...f, marks: Number(e.target.value) })} />
          </div>
        </div>
        <div>
          <label className="label">Question</label>
          <textarea className="input min-h-[80px]" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} />
        </div>
        <div>
          <label className="label">Reading passage (optional, shown above the question)</label>
          <textarea className="input min-h-[60px]" value={f.passage ?? ""} onChange={(e) => setF({ ...f, passage: e.target.value })} />
        </div>
        {(f.kind === "mcq" || f.kind === "ranking" || f.kind === "multi_text" || f.kind === "multi_select") && (
          <div>
            <label className="label">
              {f.kind === "mcq"
                ? "Options (one per line)"
                : f.kind === "ranking"
                  ? "Items to order (one per line, scrambled)"
                  : f.kind === "multi_select"
                    ? "Options (one per line; the number to pick = number of correct answers)"
                    : "Input labels (one per line)"}
            </label>
            <textarea className="input min-h-[80px]" value={f.optionsText} onChange={(e) => setF({ ...f, optionsText: e.target.value })} />
          </div>
        )}
        {f.kind !== "writing" && (
          <div>
            <label className="label">
              Correct answer{f.kind === "ranking" ? " (items comma-separated, in order)" : f.kind === "multi_text" ? " (answers separated by |, any order)" : ""}
            </label>
            <input className="input" value={f.correct_answer ?? ""} onChange={(e) => setF({ ...f, correct_answer: e.target.value })} />
          </div>
        )}
        <div>
          <label className="label">Evaluator hint {f.kind === "writing" ? "(shown on the marking screen)" : "(optional)"}</label>
          <textarea className="input" value={f.evaluator_hint ?? ""} onChange={(e) => setF({ ...f, evaluator_hint: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
          Active (included in new sittings)
        </label>
        {err && <p className="text-liaise-red text-sm">{err}</p>}
        <div className="flex justify-end gap-3">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- lookups */

function LookupsTab() {
  const qc = useQueryClient();
  const { data: lookups } = useQuery({ queryKey: ["adminLookups"], queryFn: api.manage.lookups.list });
  const [kind, setKind] = useState<LookupRow["kind"]>("home");
  const [newValue, setNewValue] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [actionError, setActionError] = useState("");
  const refresh = () => {
    setActionError("");
    qc.invalidateQueries({ queryKey: ["adminLookups"] });
    qc.invalidateQueries({ queryKey: ["lookups"] });
  };
  const onErr = (err: unknown) => setActionError(err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : String(err));

  const addMut = useMutation({
    mutationFn: () => api.manage.lookups.create({ kind, value: newValue.trim(), ord: 0, active: true }),
    onSuccess: () => { setNewValue(""); refresh(); },
    onError: onErr,
  });
  const deactivateMut = useMutation({ mutationFn: api.manage.lookups.deactivate, onSuccess: refresh, onError: onErr });
  const reactivateMut = useMutation({
    mutationFn: (l: LookupRow) => api.manage.lookups.reactivate(l.id, { kind: l.kind, value: l.value, ord: l.ord, active: true }),
    onSuccess: refresh,
    onError: onErr,
  });
  const removeMut = useMutation({ mutationFn: api.manage.lookups.remove, onSuccess: refresh, onError: onErr });

  const all = (lookups ?? []).filter((l) => l.kind === kind);
  const rows = showHidden ? all : all.filter((l) => l.active);
  const hiddenCount = all.filter((l) => !l.active).length;

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-slate-500">
        <strong>Deactivate</strong> hides an item from new assessments but keeps it on past records and lets you switch it back on.
        <strong> Remove</strong> deletes it for good, and is only allowed when nothing is using it.
      </p>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          {(["home", "region", "position"] as const).map((k) => (
            <button
              key={k}
              onClick={() => { setKind(k); setActionError(""); }}
              className={`px-3 py-1 rounded-full text-sm capitalize ${kind === k ? "bg-liaise-navy text-white" : "bg-white border border-slate-300"}`}
            >
              {k}s
            </button>
          ))}
        </div>
        {hiddenCount > 0 && (
          <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
            <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
            Show {hiddenCount} deactivated
          </label>
        )}
      </div>
      <div className="card p-4 flex gap-2">
        <input className="input" placeholder={`Add a ${kind}...`} value={newValue} onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && newValue.trim() && addMut.mutate()} />
        <button className="btn-primary" onClick={() => addMut.mutate()} disabled={!newValue.trim() || addMut.isPending}>Add</button>
      </div>
      {actionError && <p className="text-liaise-red text-sm">{actionError}</p>}
      <div className="card divide-y divide-slate-100">
        {rows.length === 0 && <p className="px-4 py-5 text-sm text-slate-400">None yet.</p>}
        {rows.map((l) => (
          <div key={l.id} className={`px-4 py-2.5 flex items-center justify-between gap-3 text-sm ${!l.active ? "bg-slate-50" : ""}`}>
            <span className={l.active ? "" : "text-slate-400"}>
              {l.value}
              {!l.active && <span className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded bg-slate-200 text-slate-500">Hidden</span>}
            </span>
            <div className="flex gap-3 shrink-0">
              {l.active ? (
                <button className="text-liaise-orange hover:underline text-xs" onClick={() => deactivateMut.mutate(l.id)} disabled={deactivateMut.isPending}>
                  Deactivate
                </button>
              ) : (
                <button className="text-liaise-green hover:underline text-xs" onClick={() => reactivateMut.mutate(l)} disabled={reactivateMut.isPending}>
                  Reactivate
                </button>
              )}
              <button
                className="text-liaise-red hover:underline text-xs"
                onClick={() => { if (confirm(`Permanently remove "${l.value}"? This cannot be undone. If it is used by any assessment it will be blocked, deactivate instead.`)) removeMut.mutate(l.id); }}
                disabled={removeMut.isPending}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- access */

type GrantRole = "auditor" | "admin" | "manager" | "recruitment";

const ROLE_LABELS: Record<GrantRole, string> = {
  manager: "Manager (one home)",
  recruitment: "Recruitment team",
  auditor: "Auditor",
  admin: "Admin",
};

function AccessTab() {
  const qc = useQueryClient();
  const { data: roles } = useQuery({ queryKey: ["adminRoles"], queryFn: api.manage.roles.list });
  const { data: lookups } = useQuery({ queryKey: ["lookups"], queryFn: api.lookups });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<GrantRole>("manager");
  const [grantHomes, setGrantHomes] = useState<string[]>([]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["adminRoles"] }); qc.invalidateQueries({ queryKey: ["me"] }); };
  const addMut = useMutation({
    mutationFn: async () => {
      if (role === "manager") {
        // one grant row per home
        for (const h of grantHomes) await api.manage.roles.add(email.trim(), "manager", h);
      } else {
        await api.manage.roles.add(email.trim(), role);
      }
    },
    onSuccess: () => { setEmail(""); setGrantHomes([]); refresh(); },
  });
  const removeMut = useMutation({ mutationFn: api.manage.roles.remove, onSuccess: refresh });
  const canGrant = email.includes("@") && (role !== "manager" || grantHomes.length > 0);

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-slate-500">
        Everyone at Liaise can create and evaluate assessments. <strong>Managers</strong> additionally see the
        assessments for their home(s), view only. <strong>Recruitment team</strong> and <strong>auditors</strong> see
        all records and reports; <strong>admins</strong> can also manage questions, lookups and access.
      </p>
      <div className="card p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          <input className="input max-w-xs" placeholder="name@liaise.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <select className="input max-w-[200px]" value={role} onChange={(e) => { setRole(e.target.value as GrantRole); setGrantHomes([]); }}>
            {(Object.keys(ROLE_LABELS) as GrantRole[]).map((k) => (
              <option key={k} value={k}>{ROLE_LABELS[k]}</option>
            ))}
          </select>
          <button className="btn-primary" onClick={() => addMut.mutate()} disabled={!canGrant || addMut.isPending}>
            {addMut.isPending ? "Granting..." : "Grant"}
          </button>
        </div>
        {role === "manager" && (
          <div>
            <div className="label">Which home(s)? One grant is created per home.</div>
            <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto border border-slate-200 rounded-lg p-2">
              {(lookups?.home ?? []).map((h) => {
                const on = grantHomes.includes(h.value);
                return (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => setGrantHomes((g) => (on ? g.filter((x) => x !== h.value) : [...g, h.value]))}
                    className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-liaise-navy text-white border-liaise-navy" : "bg-white text-slate-600 border-slate-300 hover:border-liaise-navy"}`}
                  >
                    {h.value}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {addMut.error && <p className="text-liaise-red text-sm">{String(addMut.error)}</p>}
      </div>
      <div className="card divide-y divide-slate-100">
        {(roles ?? []).map((r) => (
          <div key={r.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
            <span>
              {r.email}
              <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${r.role === "admin" ? "bg-liaise-navy text-white" : "bg-liaise-navy-50 text-liaise-navy"}`}>
                {r.role}
              </span>
              {r.home && <span className="ml-1.5 text-xs text-slate-500">{r.home}</span>}
            </span>
            <button className="text-liaise-red hover:underline text-xs" onClick={() => removeMut.mutate(r.id)}>Revoke</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- settings */

const SETTING_META: Record<string, { label: string; hint: string }> = {
  email_enabled: { label: "Email reports", hint: "1 = send the report email after evaluation, 0 = off" },
  report_recipients: { label: "Extra report recipients", hint: "Comma-separated emails, always in addition to the evaluator" },
  token_ttl_days: { label: "Candidate link validity (days)", hint: "Days until the link expires; candidates must still start within the allowed days and hours" },
  candidate_welcome_text: { label: "Candidate welcome text", hint: "Shown on the quiz start screen" },
  quiz_time_limit_minutes: { label: "Quiz time limit (minutes)", hint: "Timer starts when the candidate presses Start; answers auto-submit at zero. 0 = no timer" },
  test_window_enabled: { label: "Restrict when the test can be taken", hint: "1 = only during the days/times below (UK time), 0 = restrictions disabled; the saved schedule will not be enforced" },
  test_window_days: { label: "Allowed days", hint: "Comma-separated short names, e.g. Mon,Tue,Wed,Thu,Fri" },
  test_window_start: { label: "Opens at", hint: "24h UK time, e.g. 08:00" },
  test_window_end: { label: "Closes at", hint: "24h UK time, e.g. 18:00 - candidates already mid-test can finish" },
  shuffle_questions: { label: "Rotate question order", hint: "1 = each sitting sees questions in a different order (writing stays last in its section)" },
  shuffle_options: { label: "Rotate answer options", hint: "1 = each sitting sees options in a different order" },
  telemetry_retention_days: { label: "Telemetry retention (days)", hint: "Sitting activity logs older than this are deleted automatically" },
  candidate_privacy_notice: { label: "Candidate privacy notice", hint: "Shown before the test; lists exactly what is captured. Candidate must agree to start" },
  candidate_declaration: { label: "Candidate declaration", hint: "The own-work/identity statement the candidate must tick" },
  watermark_enabled: { label: "Watermark the test (1/0)", hint: "Overlays the candidate's name + Test ID faintly across the paper so captures are traceable" },
  candidate_thankyou_text: { label: "Thank-you message", hint: "Shown after submission. {name} is replaced with the candidate's first name" },
  manager_note: { label: "Note for managers", hint: "Extra guidance shown to staff on the New assessment page (the test window and link duration are already shown automatically)" },
};

function SettingsTab() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["adminSettings"], queryFn: api.manage.settings.list });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const saveMut = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => api.manage.settings.set(key, value),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["adminSettings"] }),
        qc.invalidateQueries({ queryKey: ["me"] }),
      ]);
    },
  });

  return (
    <div className="space-y-4 max-w-2xl">
      {saveMut.error && <p role="alert" className="text-sm text-liaise-red">Could not save the setting: {saveMut.error.message}</p>}
      {saveMut.isSuccess && <p role="status" className="text-sm text-liaise-green">Setting saved.</p>}
      {(settings ?? []).map((s) => {
        const meta = SETTING_META[s.skey] ?? { label: s.skey, hint: "" };
        const value = draft[s.skey] ?? s.svalue ?? "";
        const dirty = draft[s.skey] != null && draft[s.skey] !== (s.svalue ?? "");
        return (
          <div key={s.skey} className="card p-4">
            <label className="label" htmlFor={`setting-${s.skey}`}>{meta.label}</label>
            {["candidate_welcome_text", "candidate_privacy_notice", "candidate_declaration", "candidate_thankyou_text", "manager_note"].includes(s.skey) ? (
              <textarea id={`setting-${s.skey}`} aria-describedby={`hint-${s.skey}`} className="input min-h-[70px]" value={value} onChange={(e) => setDraft({ ...draft, [s.skey]: e.target.value })} />
            ) : (
              <input id={`setting-${s.skey}`} aria-describedby={`hint-${s.skey}`} className="input" value={value} onChange={(e) => setDraft({ ...draft, [s.skey]: e.target.value })} />
            )}
            <div className="flex items-center justify-between mt-2">
              <p id={`hint-${s.skey}`} className="text-xs text-slate-600">{meta.hint}</p>
              {dirty && (
                <button className="btn-primary text-xs" onClick={() => saveMut.mutate({ key: s.skey, value })} disabled={saveMut.isPending}>
                  Save
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
